"use client";

/**
 * Global Error Boundary for Next.js App Router
 * 
 * This component catches errors that occur during rendering, in lifecycle methods,
 * and in constructors of the whole tree below it.
 * 
 * It integrates with Sentry to capture errors and provides a user-friendly
 * fallback UI with retry and refresh actions.
 */

import { useEffect } from "react";

type ErrorBoundaryProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function ErrorBoundary({ error, reset }: ErrorBoundaryProps): JSX.Element {
  useEffect(() => {
    // Capture error in Sentry if available
    if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_SENTRY_DSN) {
      import("@sentry/nextjs").then((Sentry) => {
        Sentry.captureException(error, {
          tags: {
            errorBoundary: true,
          },
          extra: {
            digest: error.digest,
          },
        });
      }).catch(() => {
        // Silently fail if Sentry is not available
      });
    }
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900 px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100 mb-2">
            Something went wrong
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            We encountered an unexpected error. Please try again.
          </p>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-6 text-left">
          <p className="text-xs font-mono text-slate-500 dark:text-slate-400 break-all">
            {error.message || "An unknown error occurred"}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={reset}
            className="px-6 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg font-medium hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.href = "/";
              }
            }}
            className="px-6 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
          >
            Go to home
          </button>
        </div>
      </div>
    </div>
  );
}
